/**
 * High scores (docs/spec/04 §6): the Supernova and Classic tables - rank, score, initials, stage
 * reached - with a new entry flashing.
 */

import type { ScoreEntry, TableId } from '../../game/hiscores';
import { ORANGE, PINK, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { UiScene } from '../kit/base';
import { CONTENT_TOP } from '../kit/chrome';
import { drawTabs, TAB_H, tabAt, tabRects } from '../kit/tabs';
import { ACCENT, CELL, CX, cycle, FAINT, INK, LEFT, MUTED, RAINBOW, RIGHT, SELECTED } from '../kit/theme';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { HEADINGS, placeLine, SCORE_COLS, scoreLine, TABLE_TABS, tabOf } from './hiscoresModel';
import type { ScoreLine } from './hiscoresModel';

const LABELS = TABLE_TABS.map((t) => t.label);
const TABS = tabRects(LABELS, CONTENT_TOP);
const HEAD_Y = 50;
const ROW0 = 68;
const ROW_H = 16;
const PLACE_Y = 240;
const MEDALS = [YELLOW, ORANGE, PINK];
/** Lines appear one after another, this far apart, when a table opens. */
const CASCADE_MS = 40;

export class HiscoresScene extends UiScene {
  protected readonly heading = 'HIGH SCORES';
  protected override readonly track = 'hiscore' as const;
  private tab: number;
  private since = 0;
  private hotTab = -1;
  private readonly highlightTab: number;

  constructor(host: SceneHost, table: TableId | undefined, private readonly highlight: number | undefined) {
    super(host);
    this.tab = tabOf(table);
    this.highlightTab = this.tab;
  }

  private get table(): TableId {
    return TABLE_TABS[this.tab]?.id ?? 'supernova';
  }

  override enter(): void {
    super.enter();
    this.since = 0;
  }

  protected override describe(): string {
    const top = this.services.scores.table(this.table)[0];
    const best = top ? `Best: ${top.initials}, ${top.score}, stage ${top.stage}` : 'Empty';
    return `High scores, ${LABELS[this.tab] ?? ''}. ${best}`;
  }

  protected override hint(): string {
    return `◄► TABLE   ${this.words().back} BACK`;
  }

  protected override touchHint(): string {
    return 'TAP A TAB TO SWITCH';
  }

  protected override tick(dt: number): void {
    this.since += dt;
  }

  protected paint(screen: Screen): void {
    drawTabs(screen, LABELS, this.tab, TABS, true, this.ms, this.hotTab);
    this.paintLine(screen, HEADINGS, HEAD_Y, [MUTED, MUTED, MUTED, MUTED]);
    screen.fill(LEFT, HEAD_Y + 10, RIGHT - LEFT, 1, FAINT);
    const rows = this.services.scores.table(this.table);
    if (rows.length === 0) screen.text('NO SCORES YET', CX, ROW0 + 4 * ROW_H, MUTED, 'center');
    rows.forEach((e, i) => this.paintRow(screen, e, i));
    const mine = this.tab === this.highlightTab ? this.highlight : undefined;
    if (mine !== undefined && rows[mine]) screen.text(placeLine(mine), CX, PLACE_Y, SELECTED, 'center');
  }

  private paintRow(screen: Screen, e: ScoreEntry, i: number): void {
    if (this.since < i * CASCADE_MS) return;
    const y = ROW0 + i * ROW_H;
    const hot = this.tab === this.highlightTab && i === this.highlight;
    const flash = cycle(RAINBOW, this.ms, 90);
    const colours = hot ? [flash, flash, flash, flash] : [MEDALS[i] ?? MUTED, INK, ACCENT, INK];
    this.paintLine(screen, scoreLine(i, e), y, colours);
    if (hot) screen.text('►', SCORE_COLS.marker * CELL, y, flash);
  }

  /** One line in its four columns: rank, score, initials, stage. */
  private paintLine(screen: Screen, l: ScoreLine, y: number, colours: readonly number[]): void {
    const [rank = INK, score = INK, initials = INK, stage = INK] = colours;
    screen.text(l.rank, SCORE_COLS.rankEnd * CELL, y, rank, 'right');
    screen.text(l.score, SCORE_COLS.scoreEnd * CELL, y, score, 'right');
    screen.text(l.initials, SCORE_COLS.initials * CELL, y, initials);
    screen.text(l.stage, SCORE_COLS.stageEnd * CELL, y, stage, 'right');
  }

  private setTab(tab: number): void {
    if (tab === this.tab || tab < 0 || tab >= TABLE_TABS.length) return this.sound('error');
    this.tab = tab;
    this.since = 0;
    this.sound('move');
    this.say(this.describe());
  }

  protected onAction(a: MenuAction): void {
    if (a === 'left') this.setTab(this.tab - 1);
    else if (a === 'right') this.setTab(this.tab + 1);
    else if (a === 'confirm') this.back();
  }

  protected override onPointer(p: PointerInput): void {
    // A thumb is taller than a tab: a press a little above or below still counts.
    const near = p.y >= CONTENT_TOP - 4 && p.y < CONTENT_TOP + TAB_H + 6;
    const t = near ? tabAt(TABS, p.x, CONTENT_TOP) : -1;
    if (p.kind === 'move') this.hotTab = t;
    else if (p.kind === 'down' && p.button === 0 && t >= 0 && t !== this.tab) this.setTab(t);
    else if (p.kind === 'wheel') this.setTab(this.tab + Math.sign(p.delta));
  }
}
